import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
    MegaphoneIcon,
    PaperPlaneTiltIcon,
    UsersThreeIcon,
    CheckCircleIcon,
    WarningIcon,
    ClockCounterClockwiseIcon,
} from "@phosphor-icons/react";

const AUDIENCES = [
    { value: "contact_messages", label: "Contact-form submitters" },
    { value: "support_tickets", label: "Support-ticket submitters" },
    { value: "delete_requests", label: "Account-deletion requesters" },
    { value: "all", label: "Everyone (deduplicated)" },
];

export default function BroadcastsPanel() {
    const [audience, setAudience] = useState("contact_messages");
    const [subject, setSubject] = useState("");
    const [heading, setHeading] = useState("");
    const [bodyHtml, setBodyHtml] = useState("");
    const [preview, setPreview] = useState(null);
    const [history, setHistory] = useState([]);
    const [sending, setSending] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [success, setSuccess] = useState(null);
    const [err, setErr] = useState("");

    const refreshPreview = useCallback(async (aud) => {
        try {
            const { data } = await api.get(`/admin/broadcast/preview?audience=${aud}`);
            setPreview(data);
        } catch {
            setPreview(null);
        }
    }, []);

    const refreshHistory = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/broadcast/history");
            setHistory(data);
        } catch {
            setHistory([]);
        }
    }, []);

    useEffect(() => {
        refreshPreview(audience);
    }, [audience, refreshPreview]);

    useEffect(() => {
        refreshHistory();
    }, [refreshHistory]);

    const onSend = async () => {
        if (!subject.trim() || !heading.trim() || bodyHtml.trim().length < 10) {
            setErr("Subject, heading and body are required (body min 10 chars).");
            return;
        }
        setConfirming(true);
    };

    const confirmSend = async () => {
        setConfirming(false);
        setErr("");
        setSending(true);
        try {
            const { data } = await api.post("/admin/broadcast", {
                audience,
                subject,
                heading,
                body_html: bodyHtml,
            });
            setSuccess(data);
            setSubject("");
            setHeading("");
            setBodyHtml("");
            await refreshHistory();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Composer */}
            <div className="rounded-3xl border border-white/10 bg-surface p-5 md:p-7">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-2xl bg-primary/15 flex items-center justify-center">
                        <MegaphoneIcon size={22} weight="fill" color="#E01A4F" />
                    </div>
                    <div>
                        <h2 className="font-heading font-bold text-lg">Send a broadcast</h2>
                        <p className="text-text-secondary text-xs mt-0.5">
                            One-click email blast to past contacts. Use carefully.
                        </p>
                    </div>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-secondary font-bold">
                            Audience
                        </label>
                        <select
                            value={audience}
                            onChange={(e) => setAudience(e.target.value)}
                            data-testid="broadcast-audience"
                            className="input-field mt-2"
                        >
                            {AUDIENCES.map((a) => (
                                <option key={a.value} value={a.value}>{a.label}</option>
                            ))}
                        </select>
                        {preview && (
                            <div
                                className="mt-2 flex items-center gap-2 text-sm text-text-secondary"
                                data-testid="broadcast-preview-count"
                            >
                                <UsersThreeIcon size={16} weight="fill" color="#FFA700" />
                                <span>
                                    <span className="text-white font-bold">{preview.recipient_count}</span>{" "}
                                    unique recipient(s) will receive this
                                </span>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-secondary font-bold">
                            Email subject
                        </label>
                        <input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="e.g. India Reels app is live on Play Store!"
                            data-testid="broadcast-subject"
                            className="input-field mt-2"
                            maxLength={140}
                        />
                    </div>

                    <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-secondary font-bold">
                            Email heading (shown inside the email)
                        </label>
                        <input
                            value={heading}
                            onChange={(e) => setHeading(e.target.value)}
                            placeholder="e.g. We&apos;re live!"
                            data-testid="broadcast-heading"
                            className="input-field mt-2"
                            maxLength={120}
                        />
                    </div>

                    <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-secondary font-bold">
                            Body (HTML allowed — basic tags only)
                        </label>
                        <textarea
                            value={bodyHtml}
                            onChange={(e) => setBodyHtml(e.target.value)}
                            rows={8}
                            placeholder='<p>Hi there,</p><p>Big news...</p>'
                            data-testid="broadcast-body"
                            className="input-field mt-2 resize-none font-mono text-sm"
                            maxLength={10000}
                        />
                        <div className="text-right text-[11px] text-text-secondary mt-1">
                            {bodyHtml.length}/10000 chars · HTML tags rendered in email
                        </div>
                    </div>

                    {err && (
                        <div className="text-primary text-sm bg-primary/10 px-4 py-3 rounded-xl" data-testid="broadcast-error">
                            {err}
                        </div>
                    )}

                    {success && (
                        <div className="text-sm bg-secondary/10 border border-secondary/30 px-4 py-3 rounded-xl flex items-start gap-3" data-testid="broadcast-success">
                            <CheckCircleIcon size={20} weight="fill" color="#FFA700" className="shrink-0 mt-0.5" />
                            <div>
                                <div className="font-heading font-bold text-secondary">
                                    Broadcast complete
                                </div>
                                <div className="text-white/80 mt-0.5">
                                    Sent to {success.sent_count}/{success.recipient_count}.{" "}
                                    {success.failed_count > 0 && (
                                        <span className="text-primary">{success.failed_count} failed.</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={onSend}
                        disabled={sending || !preview || preview.recipient_count === 0}
                        data-testid="broadcast-send-btn"
                        className="btn-primary px-6 py-3 inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        <PaperPlaneTiltIcon size={18} weight="fill" />
                        {sending
                            ? "Sending..."
                            : `Send to ${preview?.recipient_count ?? 0} recipients`}
                    </button>
                </div>
            </div>

            {/* History */}
            <div className="rounded-3xl border border-white/10 bg-surface p-5 md:p-7">
                <div className="flex items-center gap-3 mb-4">
                    <ClockCounterClockwiseIcon size={20} weight="duotone" color="#FFA700" />
                    <h2 className="font-heading font-bold text-lg">Broadcast history</h2>
                </div>
                {history.length === 0 ? (
                    <div className="text-text-secondary text-sm py-6 text-center">
                        No broadcasts sent yet.
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="broadcast-history">
                        {history.map((h) => (
                            <div
                                key={h.id}
                                className="border border-white/10 rounded-2xl p-4 bg-bg/40"
                                data-testid={`broadcast-${h.id}`}
                            >
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-heading font-bold text-sm truncate">
                                            {h.subject}
                                        </div>
                                        <div className="text-[11px] text-text-secondary mt-1">
                                            {AUDIENCES.find((a) => a.value === h.audience)?.label || h.audience}
                                            {" · "}
                                            {new Date(h.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs">
                                        <span className="px-2 py-1 rounded-full bg-secondary/15 text-secondary font-bold">
                                            ✓ {h.sent_count}
                                        </span>
                                        {h.failed_count > 0 && (
                                            <span className="px-2 py-1 rounded-full bg-primary/15 text-primary font-bold flex items-center gap-1">
                                                <WarningIcon size={12} weight="fill" />
                                                {h.failed_count}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Confirm modal */}
            {confirming && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
                    <div className="bg-surface rounded-3xl p-6 max-w-md w-full border border-white/10">
                        <div className="flex items-center gap-3 mb-3">
                            <WarningIcon size={28} weight="fill" color="#FFA700" />
                            <h3 className="font-heading font-black text-xl">Confirm broadcast</h3>
                        </div>
                        <p className="text-white/80 text-sm mb-2">
                            You&apos;re about to email{" "}
                            <strong className="text-secondary">{preview?.recipient_count}</strong>{" "}
                            people. This action <strong>cannot be undone</strong>.
                        </p>
                        <p className="text-text-secondary text-xs mb-5">
                            Make sure your subject and content are final.
                        </p>
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => setConfirming(false)}
                                className="btn-ghost px-5 py-2 text-sm"
                                data-testid="broadcast-cancel-btn"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmSend}
                                className="btn-primary px-5 py-2 text-sm"
                                data-testid="broadcast-confirm-btn"
                            >
                                Yes, send to {preview?.recipient_count}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
